import {compute, signal} from "@targoninc/jess";
import type {PageInfo} from "./pageInfo.ts";
import {Api} from "./api.ts";

function findPageByFilename(pageList: PageInfo[], filename: string): PageInfo | null {
    for (const page of pageList) {
        const cleanFilename = page.filename.replaceAll("\\", "/");
        const cleanTarget = filename.replaceAll("\\", "/");
        if (cleanFilename === cleanTarget) {
            return page;
        }
        const child = findPageByFilename(page.children, filename);
        if (child) return child;
    }
    return null;
}

export const pages = signal<PageInfo[]>([]);
export const currentPage = signal<string>("index.md");
export const currentPageContent = compute((page, pagesList) => {
    const foundPage = findPageByFilename(pagesList, page);
    return foundPage?.content || "# Page Not Found\n\nThe requested page could not be found.";
}, currentPage, pages);
export const search = signal("");
export const searchSelectedIndex = signal(0);
export const searchModalOpen = signal(false);
export const mobileMenuOpen = signal(false);
export const searchResults = compute((ps, s) => {
    searchSelectedIndex.value = 0;
    if (!s) return [];

    const results: { page: PageInfo, excerpt: string }[] = [];

    function searchInPages(pagesList: PageInfo[]) {
        if (!pagesList) return;
        for (const page of pagesList) {
            const titleMatch = page.title.toLowerCase().includes(s.toLowerCase());
            const contentIndex = page.content?.toLowerCase().indexOf(s.toLowerCase()) ?? -1;

            if (titleMatch || contentIndex !== -1) {
                let excerptStr = "";
                if (contentIndex !== -1 && page.content) {
                    const start = Math.max(0, contentIndex - 60);
                    const end = Math.min(page.content.length, contentIndex + s.length + 60);
                    excerptStr = page.content.substring(start, end);
                    if (start > 0) excerptStr = "[...] " + excerptStr;
                    if (end < page.content.length) excerptStr = excerptStr + " [...]";
                } else if (page.content) {
                    const firstLine = page.content.split("\n")[0] || "";
                    excerptStr = firstLine.substring(0, 100) + (firstLine.length > 100 ? " [...]" : "");
                }
                results.push({ page, excerpt: excerptStr });
            }
            if (page.children) {
                searchInPages(page.children);
            }
        }
    }

    searchInPages(ps);
    return results;
}, pages, search);

export const flatPages = compute((ps) => {
    const flat: PageInfo[] = [];

    function flatten(pages: PageInfo[]) {
        for (const page of pages) {
            flat.push(page);
            flatten(page.children);
        }
    }

    flatten(ps);
    return flat;
}, pages);

export const nextAndPrev = compute((ps, current) => {
    if (ps.length === 0) return { next: null, prev: null };

    const index = ps.findIndex(p => p.filename.replaceAll("\\", "/") === current.replaceAll("\\", "/"));
    if (index === -1) return { next: ps[0], prev: ps[ps.length - 1] };

    const nextIndex = (index + 1) % ps.length;
    const prevIndex = (index - 1 + ps.length) % ps.length;

    return {
        next: ps[nextIndex],
        prev: ps[prevIndex]
    };
}, flatPages, currentPage);

export const filteredPages = compute((ps) => {
    return ps;
}, pages);

function getCurrentPage() {
    const url = new URL(window.location.href);
    const pathname = url.pathname;

    if (pathname.length > 1) {
        const page = pathname.substring(1);

        if (page.endsWith(".md")) {
            return page;
        }

        return `${page}.md`;
    }

    return "index.md";
}

let isNavigating = false;

window.addEventListener("popstate", () => {
    isNavigating = true;
    currentPage.value = getCurrentPage();
    isNavigating = false;
});

currentPage.subscribe((page, changed) => {
    if (!changed || isNavigating) {
        return;
    }

    const url = new URL(window.location.href);

    if (page === "index.md") {
        url.pathname = "/";
    } else {
        let pathPage = page;
        if (pathPage.endsWith(".md")) {
            pathPage = pathPage.substring(0, pathPage.length - 3);
        }
        url.pathname = `/${pathPage}`;
    }
    window.history.pushState(null, "", url);
});

Api.getPages().then(newPages => {
    pages.value = newPages;
    const initialPage = getCurrentPage();
    if (currentPage.value !== initialPage) {
        currentPage.value = initialPage;
    }
});
